import React, { useState, useEffect } from 'react';
import { Ticket, ArrowRight, Circle, CircleDot, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

const CATEGORIA_LABELS = {
    acceso_remoto: 'Acceso Remoto',
    biometrico: 'Biométrico',
    conectividad: 'Conectividad',
    cuentas_seguridad: 'Cuentas y Seguridad',
    email: 'Email',
    fuerza_movil: 'Fuerza Móvil',
    hardware: 'Hardware',
    impresora: 'Impresora',
    profit: 'Profit',
    recursos_compartidos: 'Recursos Compartidos',
    software: 'Software',
    spi: 'SPI',
    telefonia: 'Telefonía y Com.',
};

const STATUS_CONFIG = {
    abierto: { label: 'Abierto', icon: Circle, color: 'text-blue-500', dot: 'bg-blue-500' },
    en_progreso: { label: 'En progreso', icon: CircleDot, color: 'text-amber-500', dot: 'bg-amber-500' },
};

const TicketsPendientesWidget = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const key = 'widget_dismiss_tickets';
        if (localStorage.getItem(key)) setDismissed(true);
    }, []);

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            const response = await axiosInstance.get('/api/tickets');
            const open = response.data.filter(t => t.status !== 'cerrado');
            setTickets(open);
        } catch (error) {
            console.log('Tickets API not available');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        if (window.confirm('¿Deseas ocultar este widget por hoy?')) {
            localStorage.setItem('widget_dismiss_tickets', new Date().toDateString());
            setDismissed(true);
        }
    };

    if (loading || tickets.length === 0 || dismissed) return null;

    const abiertos = tickets.filter(t => t.status === 'abierto').length;
    const enProgreso = tickets.filter(t => t.status === 'en_progreso').length;

    return (
        <section className="relative rounded-2xl overflow-hidden border border-blue-200 shadow-sm group">
            {/* Dismiss */}
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                title="Ocultar por hoy"
            >
                <X size={14} />
            </button>

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-4 text-white">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Ticket size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Mis Tickets</h3>
                        <p className="text-white/80 text-sm">
                            {tickets.length} {tickets.length === 1 ? 'ticket pendiente' : 'tickets pendientes'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="bg-blue-50 p-5">
                {/* Counters */}
                <div className="flex gap-3 mb-4">
                    {abiertos > 0 && (
                        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                            <span className="text-sm font-medium text-gray-700">{abiertos} {abiertos === 1 ? 'abierto' : 'abiertos'}</span>
                        </div>
                    )}
                    {enProgreso > 0 && (
                        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                            <span className="text-sm font-medium text-gray-700">{enProgreso} en progreso</span>
                        </div>
                    )}
                </div>

                {/* Ticket list (max 3) */}
                <div className="space-y-2 mb-4">
                    {tickets.slice(0, 3).map(ticket => {
                        const cfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.abierto;
                        const Icon = cfg.icon;
                        return (
                            <div
                                key={ticket.id}
                                className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => navigate('/soporte-ti')}
                            >
                                <Icon size={16} className={cfg.color} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{ticket.titulo}</p>
                                    <p className="text-xs text-gray-400">{CATEGORIA_LABELS[ticket.categoria] || ticket.categoria}</p>
                                </div>
                                {ticket.comment_count > 0 && (
                                    <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
                                        {ticket.comment_count} {ticket.comment_count === 1 ? 'resp' : 'resp'}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                    {tickets.length > 3 && (
                        <p className="text-xs text-gray-400 text-center pt-1">
                            y {tickets.length - 3} más...
                        </p>
                    )}
                </div>

                <button
                    onClick={() => navigate('/soporte-ti')}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
                >
                    Ver mis tickets
                    <ArrowRight size={16} />
                </button>
            </div>
        </section>
    );
};

export default TicketsPendientesWidget;
